import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useMemo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import { colorTokens, useDebounce, AppSearchField, AppFilterPill } from '@eruvaka/web-pondlogs-base';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import { useTranslation } from '@eruvaka/web-pondlogs-i18n';
import { abbreviations as staticAbbreviations } from '../data/abbreviations';
const CATEGORIES = [
    'All',
    'Feeding',
    'Growth',
    'Culture',
    'Water Quality',
    'Chemical & Minerals',
    'Organic Load',
    'Microbiology',
    'Device',
];
export const AbbreviationsTab = () => {
    const { t } = useTranslation('help');
    const [selectedCategory, setSelectedCategory] = useState('All');
    const [searchQuery, setSearchQuery] = useState('');
    const debouncedSearch = useDebounce(searchQuery, 200);
    const filtered = useMemo(() => {
        const q = debouncedSearch.toLowerCase();
        return staticAbbreviations.filter((a) => {
            const matchCat = selectedCategory === 'All' || a.category === selectedCategory;
            const matchQ = !q ||
                a.key_terms.toLowerCase().includes(q) ||
                (a.full_form ?? '').toLowerCase().includes(q) ||
                a.meaning.toLowerCase().includes(q);
            return matchCat && matchQ;
        });
    }, [selectedCategory, debouncedSearch]);
    return (_jsxs(Box, { children: [_jsxs(Box, { sx: {
                    backgroundColor: colorTokens.white,
                    border: `1px solid ${colorTokens.gray200}`,
                    borderRadius: 2,
                    px: 2,
                    py: 1.5,
                    mb: 1.5,
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.5,
                }, children: [_jsx(Box, { sx: { display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', flex: 1 }, children: CATEGORIES.map((cat) => (_jsx(AppFilterPill, { isActive: selectedCategory === cat, onClick: () => setSelectedCategory(cat), inactiveBg: "white", children: cat }, cat))) }), _jsx(Box, { sx: { flexShrink: 0, pt: '1px' }, children: _jsx(AppSearchField, { value: searchQuery, onChange: setSearchQuery, placeholder: t('abbreviations.searchPlaceholder'), background: "filled" }) })] }), filtered.length === 0 && (_jsxs(Box, { sx: {
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: 6,
                    textAlign: 'center',
                }, children: [_jsx(MenuBookIcon, { sx: { fontSize: 48, color: colorTokens.gray300, mb: 1.5 } }), _jsx(Typography, { variant: "subtitle1", sx: { color: colorTokens.gray600, fontWeight: 600 }, children: "No abbreviations match your search." })] })), filtered.length > 0 && (_jsx(TableContainer, { component: Paper, sx: {
                    border: `1px solid ${colorTokens.gray200}`,
                    borderRadius: 2,
                    boxShadow: 'none',
                    overflow: 'hidden',
                }, children: _jsxs(Table, { size: "small", sx: { borderCollapse: 'collapse' }, children: [_jsx(TableHead, { children: _jsx(TableRow, { sx: { backgroundColor: colorTokens.gray50 }, children: ['Abbreviation', 'Full Form', 'Description', 'Category'].map((h) => (_jsx(TableCell, { sx: {
                                        fontWeight: 700,
                                        color: colorTokens.gray600,
                                        fontSize: 12,
                                        px: 2,
                                        py: 1.25,
                                        whiteSpace: 'nowrap',
                                        borderBottom: `1px solid ${colorTokens.gray200}`,
                                    }, children: h }, h))) }) }), _jsx(TableBody, { children: filtered.map((abbr) => (_jsxs(TableRow, { sx: {
                                    borderTop: `1px solid ${colorTokens.gray200}`,
                                    '&:hover': { backgroundColor: colorTokens.gray50 },
                                }, children: [_jsx(TableCell, { sx: { px: 2, py: 1.25, borderBottom: 'none' }, children: _jsx(Chip, { label: abbr.key_terms, size: "small", sx: {
                                                fontFamily: 'monospace',
                                                fontWeight: 700,
                                                fontSize: 12,
                                                backgroundColor: `${colorTokens.primary}12`,
                                                color: colorTokens.primary,
                                                height: 22,
                                                borderRadius: '4px',
                                                '& .MuiChip-label': { px: 0.75 },
                                            } }) }), _jsx(TableCell, { sx: { px: 2, py: 1.25, borderBottom: 'none' }, children: _jsx(Typography, { variant: "body2", sx: { fontWeight: 600, color: colorTokens.gray800, fontSize: 12 }, children: abbr.full_form ?? abbr.key_terms }) }), _jsx(TableCell, { sx: { px: 2, py: 1.25, maxWidth: 420, borderBottom: 'none' }, children: _jsx(Typography, { variant: "body2", sx: { color: colorTokens.gray600, fontSize: 11, lineHeight: 1.5 }, children: abbr.meaning }) }), _jsx(TableCell, { sx: { px: 2, py: 1.25, borderBottom: 'none' }, children: abbr.category && (_jsx(Chip, { label: abbr.category, size: "small", sx: {
                                                fontSize: 10,
                                                fontWeight: 600,
                                                backgroundColor: `${colorTokens.secondary}12`,
                                                color: colorTokens.secondary,
                                                height: 20,
                                                borderRadius: '4px',
                                                '& .MuiChip-label': { px: 0.75 },
                                            } })) })] }, abbr.sno))) })] }) }))] }));
};
